import { cn } from '@restworld/utils-common';
import { circularImageIcon, rectangularImageIcon, textLengthMap } from './skeleton-loading.constants';
import { SkeletonPhotoBorderedIcon, SkeletonPhotoIcon } from '@restworld/utils-common';

export const loadingText = <span className="sr-only">Loading...</span>;

export const SkeletonParagraphLoading = () => (
  <div className="flex flex-col content-start">
    <SkeletonTextLoading width="medium" />
  </div>
);

export const SkeletonRectangularImageLoading = ({ isIcon, className }: { isIcon?: boolean; className?: string }) =>
  isIcon ? (
    <div
      className={cn(
        'flex items-center justify-center h-36 mb-4 bg-gray-300 max-w-[15rem] min-w-full rounded',
        className
      )}
    >
      {rectangularImageIcon}
    </div>
  ) : (
    <div
      className={cn(
        'flex items-center justify-center h-36 mb-4 bg-gray-300 max-w-[15rem] min-w-full rounded',
        className
      )}
    />
  );

export const SkeletonCircularImageLoading = () => circularImageIcon;

export const SkeletonCombinationCardLoading = () => (
  <div className="max-w-[15rem] flex flex-col items-center p-4">
    <SkeletonRectangularImageLoading isIcon />
    <SkeletonParagraphLoading />
  </div>
);

export const SkeletonMapDetailLoader = () => (
  <div role="status" className="flex shadow-lg rounded-2xl w-full space-y-8 md:flex-col md:space-y-0 md:space-x-8">
    <div className="flex items-center justify-center w-[70%] md:w-full h-auto md:h-24 bg-gray-300 rounded-l-2xl md:rounded-l-none md:rounded-t-2xl relative">
      <SkeletonPhotoIcon />
      <SkeletonPhotoBorderedIcon className="w-10 h-10 text-gray-400 absolute left-2 bottom-2" />
    </div>
    <div className="w-full p-4 !ml-0 !mt-0">
      <div className="h-2 bg-gray-200 rounded-full mb-2.5" />
      <div className="h-2 bg-gray-200 rounded-full mb-4" />
      <div className="h-1.5 mt-6 bg-gray-200 rounded-full" />
      <div className="flex gap-2 mt-6">
        <div className="h-6 w-[30%] bg-gray-200 rounded-full" />
        <div className="h-6 w-[30%] bg-gray-200 rounded-full" />
      </div>
    </div>
  </div>
);

export const SkeletonJobPositionCardLoader = () => (
  <div role="status" className="flex-col flex shadow-lg rounded-2xl w-full space-y-8 md:space-y-0 md:space-x-8">
    <div className="flex items-center gap-4 md:w-full h-auto rounded-l-2xl md:rounded-l-none md:rounded-t-2xl relative">
      <SkeletonPhotoBorderedIcon className="w-20 h-20 text-gray-400 pt-2 pl-2" />
      <div className="flex flex-col self-end mb-2 gap-1.5 w-1/2">
        <div className="h-2 bg-gray-200 rounded-full" />
        <div className="h-2 bg-gray-200 rounded-full" />
      </div>
    </div>
    <div className="w-full p-4 !ml-0 !mt-0">
      <div className="h-2 bg-gray-200 rounded-full mb-2.5" />
      <div className="h-2 bg-gray-200 rounded-full mb-4" />
      <div className="flex gap-2 mt-6">
        <div className="h-8 w-1/5 bg-gray-200 rounded-full" />
        <div className="h-8 w-1/5 bg-gray-200 rounded-full" />
        <div className="h-8 w-1/5 bg-gray-200 rounded-full" />
      </div>
      <div className="h-2 bg-gray-200 rounded-full mt-6 w-1/3" />
    </div>
  </div>
);

export const SkeletonEmployerCardLoading = () => (
  <div
    role="status"
    className="p-5 border border-neutral-40 rounded-2xl bg-neutral-10 flex flex-col gap-4 box-border transition-all"
  >
    <div className="flex flex-col gap-2 max-w-full md:flex-row md:items-center md:justify-between">
      <div className="flex gap-4 items-center">
        <SkeletonPhotoBorderedIcon className="w-10 h-10 text-gray-200" />
        <SkeletonTextLoading width="small" />
      </div>
    </div>
    <div className="flex gap-2 flex-wrap">
      <SkeletonTextLoading width="small" />
      <SkeletonTextLoading width="small" />
    </div>
    <div className="flex gap-2 flex-wrap">
      <SkeletonTextLoading width="medium" />
    </div>
  </div>
);

export const SkeletonEmployerCardListLoading = () => {
  return (
    <div className="grid grid-cols-1 gap-4">
      <SkeletonEmployerCardLoading />
      <SkeletonEmployerCardLoading />
    </div>
  );
};

export const SkeletonInputLoading = () => (
  <div className="flex items-center justify-center h-[1rem] bg-gray-300 w-full rounded" />
);

export const SkeletonTextLoading = ({
  width,
  className,
}: {
  width: keyof typeof textLengthMap;
  className?: string;
}) => <div className={cn('h-2 bg-gray-200 rounded-full w-full mb-4 animate-pulse', textLengthMap[width], className)} />;

export const SkeletonTitleContentLoading = () => (
  <div>
    <SkeletonTextLoading width="small" />
    <SkeletonTextLoading width="medium" />
  </div>
);

export const SkeletonTitleContentListLoading = () => (
  <div className="flex flex-col gap-4 pt-4">
    <SkeletonTitleContentLoading />
    <SkeletonTitleContentLoading />
    <SkeletonTitleContentLoading />
  </div>
);

export const SkeletonAddressLoader = () => (
  <div className="flex items-center justify-center h-10 bg-gray-300 w-full rounded" />
);
